import { p5InstanceExtensions } from "p5";
import Util from "@/module/Utils/Util";

interface deviceInterface {
  pc: number;
  sp: number;
}

interface deviceValueInterface {
  width: number;
  position: number;
}

class Eye {
  private readonly p: p5InstanceExtensions;
  private duration: number;
  private position: deviceInterface;
  private width: deviceInterface;
  private animationCount: number;
  private countValue: number;
  private upperEyelid: number;
  private lowerEyelid: number;
  private openFlag: boolean;
  private openCallback: () => void;
  private closeCallback: () => void;
  private animationEndFlag: boolean;
  private readonly bezier: any;
  private designViewPort: number;
  private readonly frameRate: number;
  private deviceValue: deviceValueInterface;
  private lastDevice: boolean | null;

  constructor(
    p: p5InstanceExtensions,
    duration: number,
    position: deviceInterface | null = null,
    width: deviceInterface | null = null,
    frameRate: number
  ) {
    this.p = p;
    this.frameRate = frameRate;
    this.duration = this.frameRate * duration;
    this.position = position;
    this.width = width;
    this.deviceValue = {
      width: null,
      position: null,
    };
    this.animationCount = 0;
    this.upperEyelid = 0;
    this.lowerEyelid = 0;
    this.openFlag = false;
    this.animationEndFlag = true;
    this.bezier = Util.createCubicBezier();
    this.countValue = 1 / this.duration;
    this.bezier.setBezier(0.66, 0.0, 0.34, 1.0);
    this.designViewPort = Util.isPC() ? 1366 : 750;
    this.lastDevice = null;
    this.setWidthAndPosition();
    this.addEvents();
  }

  update = (width: number = window.innerWidth) => {
    if (this.animationEndFlag) return;
    // 0 <= this.animationCount <= 1
    if (this.openFlag && this.animationCount <= this.duration) {
      this.animationCount += this.countValue;
    } else if (!this.openFlag && this.animationCount > 0) {
      this.animationCount -= this.countValue;
    }
    this.blinkParams(width);
    this.fireCallback();
  };

  display = () => {
    const width = window.innerWidth / 2;
    const height = window.innerHeight / 2;
    this.update(width);
    this.p.push();
    this.p.translate(width / 2, height / 2 + width * this.deviceValue.position);
    this.p.scale(this.deviceValue.width / this.designViewPort);
    this.p.noStroke();
    this.p.beginShape();
    this.p.vertex(-width / 2, 0);
    this.p.bezierVertex(
      -width * 0.25,
      width * 0.39 * this.upperEyelid,
      width * 0.26,
      width * 0.39 * this.upperEyelid,
      width / 2,
      0
    );
    this.p.bezierVertex(
      width * 0.26,
      width * 0.39 * this.lowerEyelid,
      -width * 0.25,
      width * 0.39 * this.lowerEyelid,
      -width / 2,
      0
    );
    this.p.endShape();
    this.p.fill(0, 255, 0);
    this.p.pop();
  };

  closeEye = (cb: () => void) => {
    this.openFlag = false;
    this.animationEndFlag = false;
    this.closeCallback = cb;
  };

  openEye = (cb: () => void) => {
    this.openFlag = true;
    this.animationEndFlag = false;
    this.openCallback = cb;
  };

  blinkParams = (width: number) => {
    // animationCountを参照し、0.25 ~ 1の値にマッピング
    if (
      this.animationCount <= 1 &&
      this.animationCount >= 0 &&
      !this.animationEndFlag
    ) {
      this.upperEyelid = this.p.map(
        this.bezier.getBezier(this.animationCount),
        0,
        1,
        0.25,
        -1
      );
      this.lowerEyelid = this.p.map(
        this.bezier.getBezier(this.animationCount),
        0,
        1,
        0.25,
        1
      );
    }
  };

  fireCallback = () => {
    if (this.openFlag && this.animationCount >= 1) {
      this.upperEyelid = -1;
      this.lowerEyelid = 1;
      this.animationEndFlag = true;
      if (this.openCallback) {
        this.openCallback();
      }
    } else if (!this.openFlag && this.animationCount <= 0) {
      this.upperEyelid = 0.25;
      this.lowerEyelid = 0.25;
      this.animationEndFlag = true;
      if (this.closeCallback) {
        this.closeCallback();
      }
    }
  };

  getCloseParams = () => {
    return this.openFlag;
  };

  updateValue = (duration, position, width) => {
    this.duration = this.frameRate * duration;
    this.position = position;
    this.width = width;
    this.countValue = 1 / this.duration;
    this.designViewPort = Util.isPC() ? 1366 : 750;
    this.setWidthAndPosition();
  };

  setWidthAndPosition = () => {
    if (Util.isPC()) {
      this.deviceValue.width = this.width.pc;
      this.deviceValue.position = this.position.pc;
      this.designViewPort = 1366;
    } else {
      this.deviceValue.width = this.width.sp;
      this.deviceValue.position = this.position.sp;
      this.designViewPort = 750;
    }
    this.update();
  };

  eventSetWidthAndPosition = () => {
    if (this.lastDevice !== null && this.lastDevice === Util.isPC()) return;
    this.setWidthAndPosition();
    this.lastDevice = Util.isPC();
  };

  addEvents = () => {
    window.addEventListener("resize", this.eventSetWidthAndPosition);
  };

  destroy = () => {
    window.removeEventListener("resize", this.eventSetWidthAndPosition);
  };
}

export default Eye;
